<script setup lang="ts">
/**
 * Keyboard-shortcuts cheat sheet (NS-4 / UX review backlog #6).
 *
 * Press `?` from anywhere (except inside inputs) to open. Grouped
 * listing of every global shortcut in CWR. Same visual shell + motion
 * as CommandPalette for consistency.
 *
 * Keep this list in sync with the actual `useKeyboardShortcut` call
 * sites — §SSOT violation if shortcuts drift between modal + code.
 */
import { ref, watch, type Component } from 'vue';
import { storeToRefs } from 'pinia';
import { useKeyboardShortcutsStore } from '@/stores/keyboardShortcutsStore';
import { useFeatures } from '@/composables/useFeatures';
import {
  Search,
  Plus,
  Command,
  Keyboard,
  CornerDownLeft,
  ArrowUpDown,
  X,
} from 'lucide-vue-next';

interface Shortcut {
  keys: string[];
  label: string;
  hint?: string;
  icon?: Component;
  /** Optional feature flag gate; hides the row when the flag is off. */
  requiresFlag?: 'quickAdd' | 'globalSearch' | 'collabLoop';
}

interface ShortcutGroup {
  title: string;
  shortcuts: Shortcut[];
}

const store = useKeyboardShortcutsStore();
const { open } = storeToRefs(store);
const { flags } = useFeatures();

const rootRef = ref<HTMLDivElement | null>(null);

const groups: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      { keys: ['⌘', 'K'], label: 'Open command palette', hint: 'Fuzzy search anything', icon: Command, requiresFlag: 'globalSearch' },
      { keys: ['/'], label: 'Focus top-bar search', icon: Search, requiresFlag: 'globalSearch' },
      { keys: ['?'], label: 'Show this cheat sheet', icon: Keyboard },
      { keys: ['Esc'], label: 'Close the current overlay or modal' },
    ],
  },
  {
    title: 'Quick Add',
    shortcuts: [
      { keys: ['N'], label: 'Open the quick-add picker', icon: Plus, requiresFlag: 'quickAdd' },
      { keys: ['N', 'then', 'I'], label: 'Add an indicator (inside the picker)', requiresFlag: 'quickAdd' },
      { keys: ['N', 'then', 'A'], label: 'Add a threat actor', requiresFlag: 'quickAdd' },
      { keys: ['N', 'then', 'C'], label: 'Add a campaign', requiresFlag: 'quickAdd' },
      { keys: ['N', 'then', 'R'], label: 'Add a detection rule', requiresFlag: 'quickAdd' },
    ],
  },
  {
    title: 'Lists & Results',
    shortcuts: [
      { keys: ['↑', '↓'], label: 'Move selection up/down', icon: ArrowUpDown },
      { keys: ['Enter'], label: 'Activate the selected item', icon: CornerDownLeft },
    ],
  },
];

function isVisibleGroup(group: ShortcutGroup): boolean {
  return group.shortcuts.some(isVisibleShortcut);
}

function isVisibleShortcut(s: Shortcut): boolean {
  if (!s.requiresFlag) return true;
  return flags.value[s.requiresFlag] === true;
}

function closeModal() {
  store.closeModal();
}

function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) closeModal();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    e.preventDefault();
    closeModal();
  }
}

// Focus the root so Escape is captured even before the user clicks.
watch(open, (isOpen) => {
  if (isOpen) {
    requestAnimationFrame(() => rootRef.value?.focus());
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="cwr-ksm-enter-active"
      leave-active-class="cwr-ksm-leave-active"
      enter-from-class="cwr-ksm-enter-from"
      leave-to-class="cwr-ksm-leave-to"
    >
      <div
        v-if="open"
        ref="rootRef"
        tabindex="-1"
        class="keyboard-shortcuts-backdrop fixed inset-0 z-[60] flex items-start justify-center pt-[12vh] bg-black/70 outline-none"
        data-qa-keyboard-shortcuts-backdrop
        @click="onBackdropClick"
        @keydown="onKeydown"
      >
        <div
          class="keyboard-shortcuts-modal w-full max-w-xl bg-surface-800 border border-border-default rounded-xl shadow-2xl overflow-hidden flex flex-col"
          role="dialog"
          aria-label="Keyboard shortcuts"
          data-qa-keyboard-shortcuts-modal
        >
          <!-- Header -->
          <div class="keyboard-shortcuts-header flex items-center justify-between gap-3 px-5 py-3 border-b border-border-default">
            <div class="flex items-center gap-2">
              <Keyboard class="w-4 h-4 text-text-muted" />
              <h2 class="text-sm font-semibold text-text-primary">Keyboard shortcuts</h2>
            </div>
            <button
              type="button"
              class="keyboard-shortcuts-close p-1 rounded hover:bg-surface-700 text-text-muted cwr-motion"
              aria-label="Close"
              data-qa-keyboard-shortcuts-close
              @click="closeModal"
            >
              <X class="w-4 h-4" />
            </button>
          </div>

          <!-- Body -->
          <div class="keyboard-shortcuts-body max-h-[60vh] overflow-y-auto flex flex-col gap-5 px-5 py-4">
            <template v-for="group in groups" :key="group.title">
              <section
                v-if="isVisibleGroup(group)"
                class="keyboard-shortcuts-group flex flex-col gap-2"
                :data-qa-keyboard-shortcuts-group="group.title.toLowerCase().replace(/\s+/g, '-')"
              >
                <h3 class="keyboard-shortcuts-group-title text-[10px] uppercase tracking-wider text-text-muted">
                  {{ group.title }}
                </h3>
                <div class="keyboard-shortcuts-group-rows flex flex-col gap-1.5">
                  <template v-for="(s, idx) in group.shortcuts" :key="idx">
                    <div
                      v-if="isVisibleShortcut(s)"
                      class="keyboard-shortcuts-row flex items-center gap-3 px-2 py-1.5 rounded hover:bg-surface-700/50 cwr-motion"
                      data-qa-keyboard-shortcuts-row
                    >
                      <component
                        :is="s.icon"
                        v-if="s.icon"
                        class="keyboard-shortcuts-row-icon w-3.5 h-3.5 text-text-muted shrink-0"
                      />
                      <span
                        v-else
                        aria-hidden="true"
                        class="w-3.5 h-3.5 shrink-0"
                      />
                      <div class="flex flex-col gap-0.5 flex-1 min-w-0">
                        <span class="keyboard-shortcuts-row-label text-sm text-text-primary">
                          {{ s.label }}
                        </span>
                        <span
                          v-if="s.hint"
                          class="keyboard-shortcuts-row-hint text-[10px] text-text-muted"
                        >
                          {{ s.hint }}
                        </span>
                      </div>
                      <div class="keyboard-shortcuts-keys flex items-center gap-1 shrink-0">
                        <template v-for="(k, kIdx) in s.keys" :key="kIdx">
                          <span
                            v-if="k === 'then'"
                            class="text-[10px] text-text-disabled px-0.5"
                          >then</span>
                          <kbd
                            v-else
                            class="keyboard-shortcuts-kbd px-1.5 py-0.5 rounded border border-border-default text-[10px] font-mono text-text-secondary bg-surface-900"
                          >{{ k }}</kbd>
                        </template>
                      </div>
                    </div>
                  </template>
                </div>
              </section>
            </template>
          </div>

          <!-- Footer hint -->
          <div class="keyboard-shortcuts-footer flex items-center justify-between gap-3 px-5 py-2 border-t border-border-default text-[10px] text-text-disabled">
            <span>Tip: shortcuts work anywhere except inside text inputs (except <kbd class="px-1 py-0.5 rounded border border-border-default font-mono">⌘K</kbd>).</span>
            <span class="flex items-center gap-1">
              <kbd class="px-1 py-0.5 rounded border border-border-default font-mono">Esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.cwr-ksm-enter-active,
.cwr-ksm-leave-active {
  transition: opacity 150ms ease-out;
}
.cwr-ksm-enter-active .keyboard-shortcuts-modal,
.cwr-ksm-leave-active .keyboard-shortcuts-modal {
  transition: transform 150ms ease-out, opacity 150ms ease-out;
}
.cwr-ksm-enter-from,
.cwr-ksm-leave-to {
  opacity: 0;
}
.cwr-ksm-enter-from .keyboard-shortcuts-modal,
.cwr-ksm-leave-to .keyboard-shortcuts-modal {
  transform: translateY(-8px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .cwr-ksm-enter-active,
  .cwr-ksm-leave-active,
  .cwr-ksm-enter-active .keyboard-shortcuts-modal,
  .cwr-ksm-leave-active .keyboard-shortcuts-modal {
    transition-duration: 0ms !important;
  }
}
</style>
